import { type InputHTMLAttributes, forwardRef } from 'react';

type InputCorners = 'asymmetric' | 'asymmetric-inverse' | 'rounded' | 'sharp';

interface InputProps extends InputHTMLAttributes<HTMLInputElement> {
  label?: string;
  error?: string;
  hint?: string;
  icon?: React.ReactNode;
  rightElement?: React.ReactNode;
  corners?: InputCorners;
}

const cornerStyles: Record<InputCorners, string> = {
  'asymmetric': 'rounded-asymmetric-md',
  'asymmetric-inverse': 'rounded-asymmetric-inverse-md',
  'rounded': 'rounded-lg',
  'sharp': 'rounded-none',
};

export const Input = forwardRef<HTMLInputElement, InputProps>(
  ({ label, error, hint, icon, rightElement, corners = 'asymmetric', className = '', id, ...props }, ref) => {
    return (
      <div className="space-y-2">
        {label && (
          <label htmlFor={id} className="block text-sm font-medium text-text-secondary font-mono tracking-tight">
            {label}
          </label>
        )}
        <div className="relative">
          {icon && (
            <div className="absolute left-3 top-1/2 -translate-y-1/2 text-text-muted">
              {icon}
            </div>
          )}
          <input
            ref={ref}
            id={id}
            className={`
              w-full border border-border bg-surface
              ${cornerStyles[corners]}
              ${icon ? 'pl-10' : 'pl-4'} ${rightElement ? 'pr-10' : 'pr-4'} py-2.5
              text-text-primary placeholder:text-text-muted font-mono
              focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary
              hover:border-border-light
              disabled:cursor-not-allowed disabled:opacity-40
              transition-all duration-300
              ${error ? 'border-primary focus:border-primary focus:ring-primary' : ''}
              ${className}
            `}
            {...props}
          />
          {rightElement && (
            <div className="absolute right-3 top-1/2 -translate-y-1/2 text-text-muted">
              {rightElement}
            </div>
          )}
        </div>
        {hint && !error && <p className="text-xs text-text-muted font-mono">{hint}</p>}
        {error && <p className="text-sm text-primary font-mono">{error}</p>}
      </div>
    );
  }
);

Input.displayName = 'Input';
